import { useEffect, useRef, useState } from "react";
import { CalendarCheck, Check, Clock, Phone, ShieldCheck, UserRound } from "lucide-react";
import { PHONE, PHONE_TEL, getUtm, postBooking, track } from "../lib/api";
import { Reveal } from "./Reveal";
import { ShieldMark } from "./ShieldLogo";

const SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";
const CALENDLY_ORIGIN = "https://calendly.com";
let calendlyScriptPromise;

const loadCalendly = () => {
  if (window.Calendly) return Promise.resolve(window.Calendly);
  if (calendlyScriptPromise) return calendlyScriptPromise;
  calendlyScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(window.Calendly);
    script.onerror = () => reject(new Error("Calendly failed to load"));
    document.head.appendChild(script);
  });
  return calendlyScriptPromise;
};

const buildCalendlyUrl = () => {
  const url = new URL("https://calendly.com/showmeshield/30min");
  url.searchParams.set("background_color", "0A0A0A");
  url.searchParams.set("text_color", "FFFFFF");
  url.searchParams.set("primary_color", "A8E600");
  url.searchParams.set("hide_gdpr_banner", "1");
  const utm = getUtm();
  ["utm_source", "utm_medium", "utm_campaign"].forEach((k) => {
    if (utm[k] && utm[k].length < 255) url.searchParams.set(k, utm[k]);
  });
  return url.toString();
};

const TRUST_POINTS = [
  { icon: Clock, title: "10 Minutes", text: "Easy to fit into your day." },
  { icon: UserRound, title: "Real Person", text: "Talk with someone — not a call-center maze." },
  { icon: ShieldCheck, title: "No Pressure", text: "Start with questions, not an application." },
];

const COVER_LIST = [
  "What you're trying to protect",
  "Questions about supplemental insurance",
  "Life insurance questions",
  "Health coverage questions",
  "Areas of your current protection you don't understand",
  "Whether a longer conversation would actually be useful",
];

const NEXT_STEPS = [
  "We review what you told us.",
  "We connect at the time you selected.",
  "We listen first.",
  "If there's something worth exploring, we explain the next step.",
];

export const ScheduleSection = () => {
  const sectionRef = useRef(null);
  const widgetRef = useRef(null);
  const initializedRef = useRef(false);
  const viewedRef = useRef(false);
  const [status, setStatus] = useState("idle");
  const [booked, setBooked] = useState(false);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== CALENDLY_ORIGIN) return;
      const name = event.data?.event;
      if (name === "calendly.event_type_viewed") track("calendly_interaction");
      if (name === "calendly.date_and_time_selected") track("calendar_time_selected");
      if (name === "calendly.event_scheduled") {
        track("quick_consultation_scheduled");
        setBooked(true);
        postBooking({
          event_uri: event.data?.payload?.event?.uri || "",
          invitee_uri: event.data?.payload?.invitee?.uri || "",
          source: "calendly-embed",
          ...getUtm(),
        }).catch(() => {});
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => {
    const target = sectionRef.current;
    if (!target) return;

    const viewObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !viewedRef.current) {
          viewedRef.current = true;
          track("calendar_section_view");
        }
      },
      { threshold: 0.25 }
    );
    viewObserver.observe(target);

    const loadObserver = new IntersectionObserver(
      async ([entry]) => {
        if (!entry.isIntersecting || initializedRef.current) return;
        initializedRef.current = true;
        setStatus("loading");
        try {
          const Calendly = await loadCalendly();
          if (!widgetRef.current || !Calendly?.initInlineWidget) throw new Error("Calendly unavailable");
          Calendly.initInlineWidget({
            url: buildCalendlyUrl(),
            parentElement: widgetRef.current,
            resize: false,
          });
          setStatus("ready");
        } catch {
          initializedRef.current = false;
          setStatus("error");
        }
        loadObserver.disconnect();
      },
      { rootMargin: "600px 0px", threshold: 0.01 }
    );
    loadObserver.observe(target);

    return () => {
      viewObserver.disconnect();
      loadObserver.disconnect();
    };
  }, []);

  return (
    <section
      id="schedule"
      ref={sectionRef}
      data-testid="schedule-section"
      aria-labelledby="schedule-heading"
      className="relative overflow-hidden border-y border-line bg-ink grid-lines scroll-mt-20"
    >
      <div className="pointer-events-none absolute -left-40 bottom-0 h-96 w-96 rounded-full bg-lime/10 blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
        <div className="grid items-start gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow mb-4">Quick Consultation</p>
            <h2 id="schedule-heading" className="h-display text-3xl text-white sm:text-4xl lg:text-5xl">
              Got 10 minutes? <span className="text-lime">Let&apos;s talk it through.</span>
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-zinc-400 sm:text-lg">
              You don&apos;t need a long insurance meeting to get started. Pick a time that works for
              you and spend about 10 minutes telling us what you&apos;re trying to protect and what
              questions you have.
            </p>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-lime">
              No pressure. No obligation. Just a quick conversation.
            </p>

            <div className="mt-9 space-y-5">
              {TRUST_POINTS.map((t) => (
                <div key={t.title} className="flex items-start gap-4">
                  <t.icon className="mt-0.5 h-6 w-6 shrink-0 text-lime" strokeWidth={1.75} />
                  <div>
                    <h3 className="font-display text-base font-bold uppercase tracking-wider text-white">{t.title}</h3>
                    <p className="mt-1 text-sm text-zinc-400">{t.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-md border border-line bg-ink-3 p-6">
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-white">
                What can we cover in 10 minutes?
              </h3>
              <ul className="mt-4 space-y-2.5">
                {COVER_LIST.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-sm text-zinc-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-lime" strokeWidth={2.5} />
                    {c}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs leading-relaxed text-zinc-500">
                A quick consultation is an introductory conversation — a full review of your coverage
                can always come later if it makes sense.
              </p>
            </div>
          </Reveal>

          <Reveal delay={120}>
            {booked && (
              <div
                role="status"
                data-testid="booking-confirmation-panel"
                className="mb-6 rounded-md border border-lime/60 bg-ink-3 p-6"
              >
                <div className="flex items-center gap-3">
                  <CalendarCheck className="h-6 w-6 text-lime" strokeWidth={1.75} />
                  <h3 className="h-display text-xl text-white">You&apos;re on the calendar.</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                  Your quick consultation is scheduled. We&apos;ll use the time to understand what
                  you&apos;re looking for, answer your initial questions, and determine whether
                  there&apos;s anything worth exploring further.
                </p>
                <h4 className="mt-4 font-display text-sm font-bold uppercase tracking-wider text-white">
                  What happens next?
                </h4>
                <ol className="mt-3 space-y-2">
                  {NEXT_STEPS.map((s, i) => (
                    <li key={s} className="flex items-center gap-3 text-sm text-zinc-300">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-lime/50 font-mono text-[11px] font-bold text-lime">
                        {i + 1}
                      </span>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="overflow-hidden rounded-md border border-line bg-ink-3 shadow-[0_20px_60px_rgba(0,0,0,0.45)]">
              <div className="flex items-center gap-2.5 border-b border-line px-5 py-3.5">
                <span className="h-2 w-2 rounded-full bg-lime" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                  Quick 10-Minute Shield Consultation
                </span>
              </div>
              <div className="relative h-[700px] sm:h-[720px]">
                {status !== "ready" && status !== "error" && (
                  <div
                    data-testid="calendly-skeleton"
                    className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-ink-3"
                  >
                    <div className="animate-pulse">
                      <ShieldMark className="h-14 w-14" />
                    </div>
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                      Loading available times…
                    </p>
                  </div>
                )}
                {status === "error" && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-ink-3 px-6 text-center">
                    <p className="text-sm text-zinc-400">
                      The calendar couldn&apos;t load right now. The fastest option is always a call:
                    </p>
                    <a href={PHONE_TEL} className="btn-lime !px-5 !py-3 text-sm">
                      <Phone className="h-4 w-4" /> {PHONE}
                    </a>
                  </div>
                )}
                <div
                  ref={widgetRef}
                  data-testid="calendly-widget-container"
                  className="h-full w-full min-w-[320px]"
                />
              </div>
            </div>
            <p className="mt-4 text-center text-xs text-zinc-500">
              Choose an available time above for your 10-minute Show Me Shield consultation.
            </p>
          </Reveal>
        </div>

        <Reveal className="mt-14 text-center">
          <p className="eyebrow-dark mb-3" style={{ color: "#A8E600" }}>Don&apos;t Want to Schedule?</p>
          <p className="text-base text-zinc-400">
            If you&apos;d rather talk now, give Show Me Shield a call.
          </p>
          <a
            href={PHONE_TEL}
            data-testid="schedule-phone-link"
            onClick={() => track("phone_click", { location: "schedule_section" })}
            className="mt-4 inline-flex items-center gap-3 font-mono text-2xl font-bold text-white transition-colors hover:text-lime"
          >
            <Phone className="h-6 w-6 text-lime" /> {PHONE}
          </a>
        </Reveal>
      </div>
    </section>
  );
};
